import { AlertCircle, ClipboardCheck, Loader2, ShieldAlert, ShieldCheck, X } from "lucide-react";
import type { ThreeSecondTestInput, ThreeSecondTestResult } from "@ease/page-builder";

interface ThreeSecondTestPanelProps {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  input: ThreeSecondTestInput | null;
  result: ThreeSecondTestResult | null;
  onClose: () => void;
  onRunAgain: () => void;
}

function scoreTone(score: number) {
  if (score >= 80) {
    return "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  }

  if (score >= 60) {
    return "border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-200";
  }

  return "border-destructive/25 bg-destructive/10 text-destructive";
}

function severityTone(severity: "critical" | "warning" | "info") {
  if (severity === "critical") {
    return "border-destructive/25 bg-destructive/5 text-destructive";
  }

  if (severity === "warning") {
    return "border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-200";
  }

  return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-foreground";
}

export function ThreeSecondTestPanel({
  isOpen,
  isLoading,
  error,
  input,
  result,
  onClose,
  onRunAgain,
}: ThreeSecondTestPanelProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        aria-label="Close 3-second test panel"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-background/35 backdrop-blur-[1px]"
      />
      <aside className="fixed right-0 top-12 z-50 flex h-[calc(100vh-48px)] w-full max-w-[420px] flex-col border-l border-border bg-background shadow-2xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">AI review</p>
            <h2 className="mt-1 flex items-center gap-2 text-lg font-semibold text-foreground">
              <ClipboardCheck className="h-5 w-5 text-[var(--color-primary)]" />
              3-Second Test
            </h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Quick hero validation for clarity, CTA strength, hierarchy, and trust.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground transition hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {input ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Hero under review</p>
              <p className="mt-2 text-base font-semibold text-foreground">{input.headline}</p>
              {input.subheadline ? (
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{input.subheadline}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  CTA: {input.ctaText ?? "missing"}
                </span>
                <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  Trust: {input.trustSignals.length > 0 ? input.trustSignals.join(", ") : "none detected"}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-4 text-sm leading-6 text-muted-foreground">
              Add a hero with a real headline first. The panel will use the first hero block in the editor.
            </div>
          )}

          {isLoading ? (
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center gap-3 text-sm text-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                Running AI 3-second test...
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-2xl border border-destructive/25 bg-destructive/5 p-4 text-sm text-destructive">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          ) : null}

          {result ? (
            <>
              <div className={`rounded-2xl border p-4 ${scoreTone(result.score)}`}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]">Score</p>
                <div className="mt-2 flex items-end gap-3">
                  <span className="text-4xl font-bold">{result.score}</span>
                  <span className="pb-1 text-sm">
                    {result.score >= 80 ? "Strong first impression" : result.score >= 60 ? "Needs tightening" : "Likely losing attention fast"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">Issues</h3>
                  <span className="text-xs text-muted-foreground">{result.issues.length} findings</span>
                </div>
                {result.issues.length === 0 ? (
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-300">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>No issues returned. Hero looks strong on the tested criteria.</span>
                    </div>
                  </div>
                ) : (
                  result.issues.map((issue, index) => (
                    <div key={`${issue.criterion}-${index}`} className={`rounded-2xl border p-4 ${severityTone(issue.severity)}`}>
                      <div className="flex items-start gap-3">
                        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold capitalize">{issue.criterion}</p>
                            <span className="rounded-full bg-background/70 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.12em]">
                              {issue.severity}
                            </span>
                          </div>
                          <p className="mt-2 text-sm leading-6">{issue.suggestion}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onRunAgain}
            disabled={isLoading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
            Run 3-Second Test
          </button>
        </div>
      </aside>
    </>
  );
}
